// builder.slice.js
import { createSlice } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { extractPortfoliosFromFile } from "../../modules/Inference/inferenceApi";

const formatCash = (wgt) => ({
    id: 'custodial_cash',
    name: "Custodial Cash",
    type: 'cash',
    wgt: wgt * 100 || 0,
    _locked: true
});

const initialState = {
    portfolioBuilder: {
        id: null,
        title: '',
        type: 'm',
        items: [formatCash(0)],
        stateId: null,
    },
    precision: 2,
    builders: {}, // Changed to an object to hold builder states by ID
    fileUploads: {},
    fileUploadErrors: {},
    fileUploadsInProgress: [], // Holds ongoing uploads

    lastUploadFilename: null
};

const unwindToArray = (obj) => {
    const array = [];
    for (let key in obj) {
        array.push({
            id: key,
            name: obj[key].name,
            wgt: obj[key].wgt * 100,
            type: obj[key].type
        });
    }
    return array;
};

const builderSlice = createSlice({
    name: 'builder',
    initialState,
    reducers: {
        updatePrecision: (state, action) => {
            state.precision = action.payload;
        },
        setNewPortfolioBuilderContext: (state, action) => {
            const { type = 'm', items = [], name = '' } = action.payload;
            state.portfolioBuilder = {
                id: null,
                title: name,
                type: type,
                items: [formatCash(0), ...items],
                stateId: uuidv4()
            };
        },
        setExistingPortfolioBuilderContext: (state, action) => {
            const portfolio = action.payload;
            const items = unwindToArray(portfolio.ptfl);
            items.unshift(formatCash(portfolio.cash));
            state.portfolioBuilder = {
                id: portfolio.id,
                title: portfolio.name,
                type: portfolio.type,
                items: items,
                stateId: portfolio.id
            };
        },
        resetPortfolioBuilderContext: (state, action) => {
            state.portfolioBuilder = {
                ...initialState.portfolioBuilder,
                type: action.payload // Use passed type to reset
            };
        },
        copyAsNewPortfolioBuilderContext: (state, action) => {
            const portfolioId = action.payload;
            const portfolioToCopy = state.builders[portfolioId];

            if (!portfolioToCopy) {
                console.error('Portfolio not found');
                return;
            }

            const copiedItems = portfolioToCopy.items.map(item => ({ ...item }));
            state.portfolioBuilder = {
                id: null,
                title: `${portfolioToCopy.title} (Copy)`,
                type: portfolioToCopy.type,
                items: copiedItems,
                stateId: uuidv4(),
            };
        },
        setBuilders: (state, action) => {
            state.builders = action.payload; // Assuming this is used to fetch all builders from a source
        },

        // Newly added reducers
        saveBuilderState: (state, action) => {
            const { builderState } = action.payload;
            const stateId = builderState.stateId;
            state.builders[stateId] = builderState; // Save or update the builder state in the builders object
        },
        clearBuilderState: (state, action) => {
            const { stateId } = action.payload;
            if (state.builders[stateId]) {
                delete state.builders[stateId]; // Remove the builder state by stateId
            }
        },
        mergeBuilderStates: (state, action) => {
            const { newBuilderState, mergedIds } = action.payload;
            // Add the new merged builder state
            state.builders[newBuilderState.stateId] = newBuilderState;
            // Remove merged drafts
            mergedIds.forEach(id => {
                if (state.builders[id]) {
                    delete state.builders[id];
                }
            });
        },
        removeBuilderUpload: (state, action) => {
            const { filename } = action.payload;
            if (state.fileUploads[filename]) {
                delete state.fileUploads[filename];
                if (state.lastUploadFilename === filename) {
                    state.lastUploadFilename = null;
                }
            }
        },
        setActiveBuilder: (state, action) => {
            const { stateId } = action.payload;
            const builderToActivate = state.builders[stateId];

            if (builderToActivate) {
                state.portfolioBuilder = {
                    ...builderToActivate,
                    stateId: stateId
                };
            } else {
                console.error('Builder not found');
            }
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(extractPortfoliosFromFile.pending, (state, action) => {
                const { meta: { arg: filename } } = action;
                // Add the filename to the list of uploads in progress
                state.fileUploadsInProgress.push(filename);
            })
            // Handle success (fulfilled)
            .addCase(extractPortfoliosFromFile.fulfilled, (state, action) => {
                const { filename, data } = action.payload;
                const success = Array.isArray(data?.data);
                if (success) {
                    state.fileUploads[filename] = { ...data, success };
                    state.lastUploadFilename = filename;
                } else {
                    state.fileUploadErrors[filename] = data;
                }
                // Remove the filename from uploads in progress
                state.fileUploadsInProgress = state.fileUploadsInProgress.filter(f => f.name !== filename);
            })
            // Handle error (rejected)
            .addCase(extractPortfoliosFromFile.rejected, (state, action) => {
                const { meta: { arg: filename }, error } = action;
                state.fileUploadErrors[filename] = error;
                // Remove the filename from uploads in progress
                state.fileUploadsInProgress = state.fileUploadsInProgress.filter(f => f.name !== filename);
            });
    }
});





export const {
    setNewPortfolioBuilderContext,
    setExistingPortfolioBuilderContext,
    resetPortfolioBuilderContext,
    copyAsNewPortfolioBuilderContext,
    setBuilders,
    setActiveBuilder,
    saveBuilderState,        // Exporting the new action creators
    clearBuilderState,
    mergeBuilderStates,
    removeBuilderUpload,
    updatePrecision
} = builderSlice.actions;


export default builderSlice.reducer;


export const selectAllBuilderUploadFilenames = (state) => {
    return Object.keys(state.builder.fileUploads);
};

export const selectBuilderUploadErrors = (state) => {
    return state.builder.fileUploadErrors;
}


